import React, { ForwardedRef, useCallback, useMemo } from 'react';

import { Text, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { RouteProp, useRoute } from '@react-navigation/native';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectLayoutConfig } from 'store/config/selectors';
import { PlaybackTypes, RBProgram } from 'store/railsBuilder/reducer.types';
import {
    RecordingItem,
    RecordingMetadata,
} from 'store/recordings/reducer.types';
import { selectLanguage } from 'store/user/selectors';

import { ContentType } from 'services/data-manager/types';

import isFunction from 'utils/function';
import getCorrectProgramStartAndEndTime from 'utils/getCorrectProgramStartAndEndTime';
import getOrdinal from 'utils/getOrdinary';
import useIsLive from 'utils/hooks/useIsLive';
import {
    getDefaultMetadata,
    isRecordingItem,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';

import useCatchupDetails from 'screens/Detail/hooks/useCatchupDetails';
import useDetailsImageId from 'screens/Detail/hooks/useDetailsImageId';
import { isLive } from 'screens/Epg/utils/program';

import BaseTileImage from 'components/BaseTileImage';
import ContentMarkerIcons from 'components/ContentMarkerIcons';
import RibbonTouchable from 'components/CustomButtons/RibbonTouchable';
import Marker from 'components/Marker';
import {
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import gRibbonItemStyles from 'components/Ribbon/RibonItem/styles';
import ProgramLogo from 'components/Ribbon/components/ProgramLogo';
import ProgressBar from 'components/Ribbon/components/ProgressBar';
import { TileTitle } from 'components/Ribbon/components/TileTitle';
import useParentalRating from 'components/Ribbon/hooks/useParentalRating';

import { PLACEHOLDER_ICON_CENTER } from '../../constants';

const {
    ACCESSIBILITY: {
        ACCESSIBILITY_COLLECTION_FIRST_ASSET,
        STRIPE: { ACCESSIBILITY_STRIPE_FIRST_ASSET },
    },
} = LANGUAGE_KEYS;

type Props<T = RBProgram | RecordingItem> = {
    item: T;
    visibleItems: Record<string, boolean>;
    isCollection?: boolean;
    onPress?(item: T): void;
    testID?: string;
    railId?: string;
    index?: number;
    templateId?: string;
    totalNumberOfItems?: number;
};

function EpisodeRibbon(
    {
        item,
        visibleItems,
        onPress,
        isCollection = false,
        testID,
        railId,
        index = 0,
        templateId,
        totalNumberOfItems,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) {
    const { t } = useTranslation();
    const route: RouteProp<RouteNavigationParams, SCREEN.DETAILS> = useRoute();

    const { primary } = useSelector(selectLayoutConfig);
    const currentLanguage = useSelector(selectLanguage);

    const metadata = useMemo(
        () =>
            isRecordingItem(item)
                ? ({
                      ...getDefaultMetadata(item),
                      ...selectRecordingMetadataBaseOnLanguage(
                          item.metadata,
                          currentLanguage,
                      ),
                  } as RecordingMetadata)
                : item,
        [item, currentLanguage],
    );

    const isVod = route.params.playbackType === PlaybackTypes.vod_ed;

    const { programStartTime, programEndTime } =
        getCorrectProgramStartAndEndTime(metadata);

    const { isCatchup } = useCatchupDetails(metadata, programEndTime);

    const live = useIsLive({
        startTime: programStartTime,
        endTime: programEndTime,
    });

    const imageContentId = useDetailsImageId(item);

    const handlePress = useCallback(() => {
        if (isFunction(onPress)) {
            onPress(item);
        }
    }, [item, onPress]);

    const parentalRating = useParentalRating(metadata);

    const accessibilityLabel = useMemo(() => {
        let label = '';

        if (metadata.SeasonNumber) {
            label += `${t('HOVER_SEASON')}${metadata.SeasonNumber} `;
        }

        if (metadata.episodeNumber) {
            label += `${t('HOVER_EPISODE')}${metadata.episodeNumber} `;
        }

        if (isRecordingItem(item)) {
            label +=
                (metadata as RecordingMetadata).episode ||
                (metadata as RecordingMetadata).SeriesTitle ||
                '';
        } else {
            label += item.EpisodeTitle || item.Subtitle || item.Title || '';
        }

        label += ' ';

        const firstAssetKey = isCollection
            ? ACCESSIBILITY_COLLECTION_FIRST_ASSET
            : ACCESSIBILITY_STRIPE_FIRST_ASSET;
        const orderAssetKey = 'ACCESSIBILITY_STRIPE_%NUMBER%_ASSET';

        if (index === 0) {
            label += t(firstAssetKey, {
                number: totalNumberOfItems,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        } else {
            label += t(orderAssetKey, {
                number: getOrdinal(index + 1),
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return label;
    }, [metadata, item, index, totalNumberOfItems, t, isCollection]);

    return (
        <RibbonTouchable
            ref={forwardedRef}
            metadataType={ContentType.DetailsEpisode}
            isCollection={isCollection}
            onPress={handlePress}
            testID={testID}
            railId={railId || templateId}
            accessibilityLabel={accessibilityLabel}
            RibbonContentComponent={
                <>
                    <BaseTileImage
                        style={gStyles.flex}
                        placeHolder={PLACEHOLDER_ICON_CENTER}
                        resizeMode="cover"
                        contentId={imageContentId}
                        contentType={ImageContentType.EPISODE}
                        imageType={ImageType.EPISODE}
                        width={ImageSize.LANDSCAPE.width}
                    />
                    {!isVod && live && (
                        <ProgressBar
                            itemId={item.id}
                            visibleItems={visibleItems}
                            start={programStartTime}
                            end={programEndTime}
                            contentType={ContentType.Catchup}
                            isCollection={isCollection}
                            backgroundColor={primary}
                        />
                    )}
                    <ProgramLogo channelId={metadata.serviceRef} />
                    {!!(programStartTime && programEndTime && !isVod) && (
                        <Marker
                            startTime={programStartTime}
                            endTime={programEndTime}
                            bgColor={primary}
                            style={[
                                gRibbonItemStyles.marker,
                                live ? gRibbonItemStyles.isOnLiveCard : null,
                            ]}
                            textStyle={gRibbonItemStyles.markerText}
                        />
                    )}
                    <ContentMarkerIcons
                        shouldDisplayCatchup={isCatchup}
                        parentalRating={parentalRating}
                        isOnLiveCard={isVod ? false : isLive(metadata)}
                        isLimitedToOne
                        isOnCard
                    />
                </>
            }
            RibbonInfoComponent={
                <TileTitle
                    title={
                        <>
                            {!!metadata.SeasonNumber && (
                                <Text>
                                    {`${t('HOVER_SEASON')}${
                                        metadata.SeasonNumber
                                    } `}
                                </Text>
                            )}
                            {!!metadata.episodeNumber && (
                                <Text>
                                    {`${t('HOVER_EPISODE')}${
                                        metadata.episodeNumber
                                    } `}
                                </Text>
                            )}
                            {isRecordingItem(item)
                                ? (metadata as RecordingMetadata).episode ||
                                  (metadata as RecordingMetadata).SeriesTitle
                                : item.EpisodeTitle ||
                                  item.Subtitle ||
                                  item.Title}
                        </>
                    }
                />
            }
        />
    );
}

export default React.forwardRef(EpisodeRibbon);
